import { Event } from "../../../../Global/Base/Events";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { IAction, IMultiObjectAction } from "../../../UI.Controls/Actions/IAction";
import { INodeAction } from "../../../UI.Controls/Actions/INodeAction";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuComboItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuComboItem";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { GenericControl } from "../Controls/Control";
import { GenericControlParams } from "../Controls/ControlInterfaces";
import { IMultiActionToolbarButton } from "../XGControls/Actions/IToolbarButton";
import { NodeAction } from "../XGControls/Actions/NodeAction";
import { DesktopToolbarDropdownButton } from "../XGControls/Actions/ToolbarDropdownButton";
import { ToolbarControl } from "./ToolbarControl";

const UNLIMITED_GROUP_WIDTH = 90000;

/**
 * Parametry konstruktoru tridy {@link ToolbarGroup}.
 */
export type ToolbarGroupParams = GenericControlParams & {
    lockey?: string;
    toMenu: MenuType;
    priority?: number;
    position?: number;
    resizeGroups: () => void;
};

/**
 * Trida reprezentujici skupinu akci v toolbaru
 * http://sharepoint/vyroba/Dokumentace/Toolbar%20MW%20-%20programator.aspx
 */
export class ToolbarGroup extends ToolbarControl {
    //#region Fields
    public visibleChange: Event<ToolbarGroup, boolean> = new Event<ToolbarGroup, boolean>();

    public locKey: string;
    /** kontejner skupiny s jednotlivymi akcemi */
    public containerElement: HTMLDivElement = document.createElement("div");
    public actions: IAction[] = [];

    private _ctrlId: string = "";
    /** Typ skupiny */
    private _toMenu: MenuType;
    /** Priorita, podle ktere se urcuje misto skupiny v toolbaru */
    private _priority: number;
    /**Position je skutecne umisteni komponenty do toolbaru po prepocitani na zaklade Priority*/
    private _position: number;
    /** Nastaveni zda, se skupina dynamicky prizpusobuje sirce toolbaru, defaultne true */
    private _isDynamic: boolean = true;
    /** Tlacitko, kterym se zobrazuji akce, ktere se jiz nevlezly do zobrazeni */
    private _actionsButton: HTMLElement;
    /** Listener pro klik na tlacitko >> */
    private _actionButtonClickListener: EventListener;
    /** Kontextove menu se skrytymi akcemi */
    private _contextMenu: HTMLDivElement;
    private _dropDownMenu: DropDownMenu;
    private _nodeActions: INodeAction;
    private _maxWidth: number = 0;
    private _resizeGroups: () => void;
    //#endregion Fields

    //#region Listeners
    //#endregion Listeners

    public set maxWidth(v: number) {
        this._maxWidth = v;
    }

    public get maxWidth(): number {
        return this._maxWidth;
    }

    protected get _visible(): boolean {
        return !(this.containerElement.style.display === "none");
    }

    protected set _visible(v: boolean) {
        this.containerElement.style.display = v ? "" : "none";
    }

    public set ctrlID(v: string) {
        this._ctrlId = v;
        if (v)
            this.element.setAttribute("data-ctrlId", v);
        else
            this.element.removeAttribute("data-ctrlId");
    }

    public get ctrlID(): string {
        return this._ctrlId;
    }

    //#region Constructors
    constructor({
        element = document.createElement("div"),
        lockey,
        toMenu = MenuType.mtNone,
        priority,
        position,
        originalRightsDefinition,
        resizeGroups,
    }: ToolbarGroupParams) {
        super({ element, originalRightsDefinition });

        this._resizeGroups = resizeGroups;
        this._dropDownMenu = new DropDownMenu();
        this._nodeActions = NodeAction.createDefault();
        this._dropDownMenu.nodeAction = this._nodeActions;

        this.priority = priority;
        this._position = position;
        this.locKey = lockey;

        this.toMenu = toMenu;

        //Node group je pri vytvoreni skryta, protoze akce se do ni pridavaji az pozdeji.
        this.containerElement.style.display = "none";
        this._initialize();
    }
    //#endregion Constructors

    //#region Properties
    public set priority(priority: number) {
        this._priority = priority;
    }

    public get priority(): number {
        return this._priority;
    }

    public set toMenu(toMenu: MenuType) {
        this._toMenu = toMenu;
        this.containerElement.setAttribute("data-tomenu", MenuType[toMenu]);
        this._setDynamic();
    }

    public get toMenu(): MenuType {
        return this._toMenu;
    }

    /**
     * Nastaveni skupiny jako staticke (tzn. ze ma nemenou sirku)
     */
    public set dynamic(dynamic: boolean) {
        this._isDynamic = dynamic;
        if (dynamic) {
            this.containerElement.classList.remove("static");
        } else {
            this.containerElement.classList.add("static");
        }
    }

    public get dynamic(): boolean {
        return this._isDynamic;
    }

    public set visible(value: boolean) {
        if (this.containerElement == undefined)
            return;

        if (value !== this._visible) {
            this._visible = value;
            this.visibleChange.trigger(this, value);
        }
    }

    public get visible(): boolean {
        return this._visible;
    }

    //#endregion Properties

    //#region Methods

    /**
     * Pokud jsou vsechny podrizene akce neviditelne nebo zadnou akci nema tak natavim groupu jako skrytou
     */
    private _checkChildrenAndSetVisibility(): void {
        let newVisible = false;

        if (this.actions) {
            for (const action of this.actions) {
                // Když se jedná o MultiObjectAction tak nás zajímá pouze její ToolbarButton, protože ten je správně nastaven pomocí overridů
                if (action instanceof IMultiObjectAction) {
                    if (action.toolbarButton instanceof IMultiActionToolbarButton && action.toolbarButton.enabledVisual) {
                        newVisible = true;
                        break;
                    }
                } else {
                    if (action.visible) {
                        newVisible = true;
                        break;
                    }
                }
            }
        }
        this.visible = newVisible;
    }

    /**
     * Umisti akci na uvedeny index
     * @param action - pridavana akce
     * @param index - pozice, na kterou se ma akce vlozit
     */
    private _pushActionToIndex(action: IAction, index: number): void {
        if (this.actions.length > 0 && index < this.actions.length) {
            this.actions.splice(index, 0, action);
            this.element.insertBefore(action.toolbarButton.element, this.element.childNodes[index]);
        } else {
            this.actions.push(action);
            this.element.appendChild(action.toolbarButton.element);
        }
        action.toolbarButton.element.style.order = action.priority.toString();
    }

    /**Podle priority ziska pozici dane akce v grupe*/
    private _getActionPositionByPriority(action: IAction): number {
        let position: number = this.actions.length;
        //na zaklade Priority zjisti index, na kterej se ma akce vlozit
        if (action.priority != null) {
            for (let i = 0; i < this.actions.length; i++) {
                if (this.actions[i].priority > action.priority) {
                    position = i;
                    break;
                }
            }
        }

        return position;
    }

    //todo: MV - rozsisit tuto metodu o dalsi veci, ktere jsou vyuzity kolem jejiho volani
    public addAction(action: IAction): void {
        const position = this._getActionPositionByPriority(action);
        if (action.toMenu)
            this.toMenu = action.toMenu;
        this._pushActionToIndex(action, position);

        if (action instanceof INodeAction)
            action.parentAction = this;
        // Když se jedná o MultiObjectAction tak nás zajímá pouze její ToolbarButton, protože ten je správně nastaven pomocí overridů
        if (action instanceof IMultiObjectAction && action.toolbarButton instanceof IMultiActionToolbarButton) {
            action.toolbarButton.enabledVisualChanged.add((_sender, _e) => {
                this._checkChildrenAndSetVisibility();
            });
        } else {
            action.visibilityChanged.add((_sender, _e) => this._checkChildrenAndSetVisibility());
        }
        this._checkChildrenAndSetVisibility();

        if (this._isDynamic && this.containerElement.clientWidth > 0)
            this._resizeGroups();
    }

    /**
     * Odstrani akci z nadrizeneho elementu
     */
    public removeAction(action: IAction): void {
        this.actions?.splice(this.actions.indexOf(action), 1);

        if (action.toolbarButton && action.toolbarButton.element.parentElement)
            action.toolbarButton.element.parentElement.removeChild(action.toolbarButton.element);
        if (action instanceof INodeAction)
            action.parentAction = null;
        this._checkChildrenAndSetVisibility();
    }

    /**
     * Přidá do toolbarové skupiny separátor
     */
    public addSeparator(): void {
        const separatorElement = createElement({ classes: "toolbar-separator" });
        this.element.appendChild(separatorElement);
    }

    /**
    * Metoda, ktera se spousti pri resizovani okna.
    * Kontroluje se, zda se podrizene akce vlezou do skupiny
    */
    public checkIfActionsFitGroup(): void {
        if (!this._isDynamic)
            return;

        let actionsWidth: number = 0; //sirka vsech akci vlozenych a zobrazenych v grupe
        let showNodeActionButton = false;
        let allToNode = false;

        if (this.visible && this.maxWidth === 0)
            this.maxWidth = UNLIMITED_GROUP_WIDTH;

        for (const action of this.actions) {
            if (!action.toolbarButton) {
                continue;
            }

            const curActionWidth = action.toolbarButton.element.clientWidth;
            if (allToNode || (actionsWidth + curActionWidth + 20) > this.maxWidth) {
                this.moveActionToNode(action);
                showNodeActionButton = true;
                allToNode = true;
            } else {
                this.moveActionToGroup(action);
                actionsWidth += curActionWidth;
            }
        }

        //pocet viditelnych akci self rovna poctu akci zobrazenych v grupe
        if (this._actionsButton) {
            if (!showNodeActionButton) {
                this._actionsButton.style.display = "none";
            } else
                this._actionsButton.style.display = "";
        }
        this.element.classList.toggle("withActionsButton", showNodeActionButton);
    }

    /**
     * Přesune všechny akce do grupy - To je potřeba na začátku provést, aby měly všechny elementy svou velikost (nenulovou clientWidth).
     */
    public moveAllActionsToGroup(): void {
        for (const action of this.actions) {
            if (action.visible || action.toolbarButton) {
                this.moveActionToGroup(action);
            }
        }
    }

    /**Presune akci z nodu do grupy*/
    public moveActionToGroup(action: IAction): void {
        if (this.element !== action.toolbarButton.element.parentElement)
            this.element.appendChild(action.toolbarButton.element);

        if (action.toolbarButton instanceof DesktopToolbarDropdownButton) {
            this._dropDownMenu.removeItem(action.toolbarButton.dropDownMenuItem);
            action.toolbarButton.dropDownMenu.rootMenu = null; // root menu se musi nastavovat, protoze e tu presouva cele dropdownmenu a ne jen polozka
        } else
            this._nodeActions.removeAction(action);

        action.isInNode = false;
    }

    /**Presune akci z grupy do nodu*/
    public moveActionToNode(action: IAction): void {
        if (!this._actionsButton)
            this._createActionsButton();
        if (action.isInNode)
            return;

        this._actionsButton.style.display = "block";
        this.element.classList.add("withActionsButton");

        if (action.toolbarButton instanceof DesktopToolbarDropdownButton) {
            action.toolbarButton.dropDownMenu.removeItem(action.toolbarButton.dropDownMenuItem);
            (action.toolbarButton.dropDownMenuItem as DropDownMenuComboItem).submenu = action.toolbarButton.dropDownMenu;
            this._dropDownMenu.addItem(action.toolbarButton.dropDownMenuItem);
            action.toolbarButton.dropDownMenu
                .rootMenu = this._dropDownMenu.rootMenu ? this._dropDownMenu.rootMenu : this._dropDownMenu;
        } else
            this._nodeActions.addAction(action);

        action.isInNode = true;

        this._contextMenu.appendChild(action.toolbarButton.element);
    }

    private _initialize(): void {
        this.element.classList.add("actions");

        this.containerElement.classList.add("Group");

        // this.descElement.classList.add("description");

        this.element.style.setProperty("column-gap", "0");
        this.element.style.setProperty("-moz-column-gap", "0");
        this.element.style.setProperty("-webkit-column-gap", "0");

        if (this._position < this.element.parentElement.childNodes.length)
            this.element.parentElement.insertBefore(this.containerElement, this.element.parentElement.childNodes[this._position]);
        else
            this.element.parentElement.appendChild(this.containerElement);

        this.containerElement.appendChild(this.element);
        // this.containerElement.appendChild(this.descElement);
    }

    /**
     * Vytvoreni tlacitka pro schovane akce
     */
    private _createActionsButton(): void {
        this._actionsButton = document.createElement("div");

        this._contextMenu = document.createElement("div");

        this._actionButtonClickListener = (e: MouseEvent) => {
            if (this._actionsButton.classList.contains("Disabled"))
                return;
            EventHelper.stopPropagation(e);

            //if (this.popup == null) {
            //    this.createPopup(e);
            //    this.popup.element.classList.add("actions");
            //    this.popup.SetVisibility(false);
            //}

            //var rect = this.actionsButton.getBoundingClientRect();
            //this.popup.SetIndent(null, (rect.left - 95) + "px");

            //this.popup.SetVisibility(!this.popup.visible);
            this._dropDownMenu.show(this._actionsButton, e);
        };
        this.containerElement.insertBefore(this._actionsButton, this.element);

        this._actionsButton.classList.add("btn", "flat", "actionsButton");
        this._actionsButton.addEventListener("click", this._actionButtonClickListener);
        Localization.addLocDescData(this._actionsButton, "GUI-669369", "title"); //Další akce
    }

    /**
     * Defaultni nastaveni, zda bude skupina dynamicka na zaklade property ToMenu
     */
    private _setDynamic(): void {
        switch (this._toMenu) {
            //case MenuType.mtRecord:
            //case MenuType.mtNone:
            //case MenuType.mtPrint:
            //case MenuType.mtFormControl:
            //case MenuType.mtCancelSave:
            case MenuType.mtRecordNavigation:
            case MenuType.mtStandardAction:
                this.dynamic = false;
                break;
            default:
                this.dynamic = true;
        }
    }

    //async
    //public FindMasterNode(ctrlID: string): Promise<GenericControl[]> {
    //    var action: NodeAction = this.findAction(ctrlID);

    //    var controls: Promise<GenericControl[]> = [];

    //    if (action != null) {
    //        controls = Promise.resolve([action]);
    //        return controls;
    //    }

    //    return controls;
    //}

    /**Najde masternode*/
    public findMasterNode(ctrlID: string): GenericControl[] {
        const action: NodeAction = this._findAction(ctrlID);

        const controls: GenericControl[] = [];

        if (action != null) {
            controls.push(action);
            return controls;
        }

        return controls;
    }

    /**Najde konkretni akci*/
    private _findAction(ctrlID: string): NodeAction {
        for (const a of this.actions) {
            if (a instanceof NodeAction) {
                const action: NodeAction = a;
                if (ctrlID != null && action.ctrlID == ctrlID) {
                    return action;
                } else {
                    //Dalsi uroven hledani - prochazi prvky v nodeAction
                    const resultFromChild = action.findMasterNode(ctrlID);
                    if (resultFromChild != null)
                        return resultFromChild;
                }
            }
        }

        return null;
    }
    //#endregion Methods

    //#region dispose
    public override dispose(): void {
        this._actionsButton?.removeEventListener("click", this._actionButtonClickListener);
        this._nodeActions?.dispose();
        this._dropDownMenu?.dispose();
        super.dispose();
    }

    //#endregion dispose
}
